/**
 * Utility functions for price validation and formatting
 */

/**
 * Validates and formats a price input string
 * Handles both comma and dot as decimal separators
 * Ensures maximum 2 decimal places
 * @param input - The price input string
 * @returns The formatted price number or null if invalid
 */
export const validateAndFormatPrice = (input: string): number | null => {
  if (!input || input.trim() === "") {
    return null;
  }

  // Remove any non-numeric characters except comma and dot
  // eslint-disable-next-line no-useless-escape
  let cleanInput = input.replace(/[^0-9,\.]/g, "");

  // Handle comma as decimal separator by replacing it with dot
  cleanInput = cleanInput.replace(",", ".");

  // If there are multiple dots, keep only the last one as decimal separator
  const dotIndex = cleanInput.lastIndexOf(".");
  if (dotIndex !== -1) {
    const beforeDot = cleanInput.substring(0, dotIndex).replace(/\./g, "");
    const afterDot = cleanInput.substring(dotIndex + 1);
    cleanInput = beforeDot + "." + afterDot;
  }

  // Convert to number
  const numericValue = parseFloat(cleanInput);

  // Check if it's a valid number
  if (isNaN(numericValue) || numericValue < 0) {
    return null;
  }

  // Round to 2 decimal places
  return Math.round(numericValue * 100) / 100;
};

/**
 * Formats a price number to display format with currency symbol
 * @param price - The price number
 * @param currencyFormatter - The Intl.NumberFormat formatter
 * @returns The formatted price string
 */
export const formatPriceDisplay = (
  price: number | null | undefined,
  currencyFormatter?: Intl.NumberFormat
): string => {
  if (price === null || price === undefined) {
    return "";
  }

  if (currencyFormatter) {
    return currencyFormatter.format(price);
  }

  // Default formatting if no formatter provided
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(price);
};

/**
 * Validates price input in real-time (for input field)
 * @param input - The current input value
 * @returns Object with validation result and formatted value
 */
export const validatePriceInput = (
  input: string
): {
  isValid: boolean;
  formattedValue: string;
  numericValue: number | null;
} => {
  const numericValue = validateAndFormatPrice(input);

  return {
    isValid: numericValue !== null,
    formattedValue: numericValue !== null ? numericValue.toString() : input,
    numericValue,
  };
};
